import basicDetaislIcon from 'assets/icons/basicDetailsIcon.svg';
import conceptIcon from 'assets/icons/conceptIcon.svg';
import financialIcon from 'assets/icons/financialsIcon.svg';
import ncdetailsIcon from 'assets/icons/ncDetailsIcon.svg';
import partnersIcon from 'assets/icons/parternsIcon.svg';
import { bdTrackerAllowedRoles } from 'data/bdTracker/bdTrackerUserRoleValidations';
import { CommentRequestPayload, User } from 'Interfaces/BDPortalInterfaces';
import { observer } from 'mobx-react';
import moment from 'moment';
import 'primeicons/primeicons.css';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { Avatar } from 'primereact/avatar';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { MultiSelect } from 'primereact/multiselect';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Skeleton } from 'primereact/skeleton';
import { TabPanel, TabView } from 'primereact/tabview';
import { useState } from 'react';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import AsyncSelect from 'react-select/async';
import { toast } from 'react-toastify';
import { getBackgroundColor, truncateName } from 'utils/avatarUtilities';
import { getStatus } from 'utils/bdTrackerUtilities/checkStageUtility';
import { checkIfFunctionalityEnabled } from 'utils/facts/roleBasedFunctionalityAccess';
import { getInitials } from 'utils/getInitials';
import { DateConversionUtility } from 'utils/dateUtilities';
import ConfirmationModal from '../../../../../components/ConfirmationModal/ConfirmationModal';
import AddIterationModal from '../AddIteration/AddIterationModal';
import HistoryTab from '../HistorySection/HistoryTab';
import NADWaiverModal from '../NADwaiver/NADWaiverModal';
import SeeMoreIteration from '../SeeMoreIteration/SeeMoreIteration';
import SiteTransferModal from '../SiteTransfer/SiteTransferModal';
import EditOpportunitySkeletonLoader from './components/skeletonLoader/editOpportunityLoader';
import EditingOpportunityLogic from './EditOpportunity.logic';
import styles from './EditOpportunity.module.scss';

// Register the Quill "maxlength" module once at module load. Previously this
// lived inside the component, so it re-registered on every render and Quill
// logged "Overwriting modules/maxlength ...". Module scope runs exactly once.
interface QuillInstance {
  on: (event: string, callback: () => void) => void;
  getText: () => string;
  deleteText: (start: number, length: number) => void;
}
interface MaxLengthOptions {
  maxLength: number;
}
Quill.register('modules/maxlength', function (quill: QuillInstance, options: MaxLengthOptions) {
  quill.on('text-change', function () {
    const textLength = quill.getText().length;
    if (textLength > options.maxLength) {
      quill.deleteText(options.maxLength, textLength - options.maxLength);
    }
  });
});

const EditOpportunity = ({
  visible,
  closeModal,
  viewType,
  opportunityIterationID,
  opportunityID,
}: {
  visible: boolean;
  closeModal: (isNewIterationCreated?: boolean) => void;
  viewType: 'Board' | 'List';
  opportunityIterationID: number;
  opportunityID: number;
}) => {
  const {
    userModulesRoles,
    isIterationActive,
    editingOpportunity,
    setEditingOpportunity,
    handleSaveTask,
    isAddIterationModalVisibile,
    isNADWaiverModalOpen,
    isSiteTransferWaiverModalOpen,
    handleOpenNADWaiverModal,
    handleOpenSiteTransferWaiverModal,
    handleCloseNADWaiverModal,
    handleCloseSiteTransferWaiverModal,
    handleOpenAddIterationModal,
    handleCloseAddIterationModal,
    comment,
    setComment,
    setEditIndex,
    newPackageSize,
    editIndex,
    setNewPackageSize,
    setSelectedConcepts,
    selectedConcepts,
    moleculeName,
    handleSaveConcept,
    isValidStatusMove,
    createPayloads,
    filterExistingConcepts,
    dropdownDetails,
    comments,
    isCommentsDataLoading,
    handleCommentChange,
    concepts,
    getConcepts,
    currentConceptsIndex,
    isOpportunityDetailsSaving,
    setCurrentConceptsIndex,
    currentCommentsIndex,
    setCurrentCommentsIndex,
    saveComments,
    saveConcept,
    isSeeMoreIterationModalOpen,
    setIsSeeMoreIterationModalOpen,
    handleOldIterationClick,
    oldIterationId,
    isOldIterationModalVisible,
    handleCloseOldIterationModal,
    iterationType,
    isConceptSaving,
    opportunityErrorDetails,
    openAccordionIndexes,
    accordionKeyIndex,
    initialOpportunity,
    handleEditOpportunityModalClose,
    handleCancelForConfirmationModal,
    handleConfirmForConfirmationModal,
    handleCheckIfSaveButtonIsDisabled,
    editOpportunityConfirmationModalVisibility,
    isCommentSaving,
    tabKey,
    isConceptsDataLoading,
    handleEditorChange,
    charCount,
    fetchSuggestionsWithDelay,
    iterationNdcPacksizeStrengths,
    isIterationNdcPacksizeStrengthsLoading,
    isPortfolioOpportunity,
  } = EditingOpportunityLogic(opportunityIterationID, closeModal, opportunityID);

  // Helper function to format numeric string with commas without Number conversion
  const formatNumberWithCommas = (numericString: string | number | null | undefined): string => {
    // Convert input to string and handle null/undefined cases
    const stringValue = String(numericString || '0');

    // Remove any non-digit characters to get clean numeric string
    const cleanNumericString = stringValue.replace(/[^0-9]/g, '');

    if (!cleanNumericString || cleanNumericString === '0') return '0';

    // Remove leading zeros but keep at least one digit
    const cleanedString = cleanNumericString.replace(/^0+/, '') || '0';

    // Add commas every 3 digits from right to left
    return cleanedString.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  const header = () => {
    return (
      <>
        {editingOpportunity ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <h4 style={{ marginTop: 0 }}>
              Iteration {concepts?.IterationCounter ? concepts?.IterationCounter : '-'}
            </h4>
            <Button
              disabled={
                !isIterationActive ||
                !checkIfFunctionalityEnabled(
                  bdTrackerAllowedRoles.addNewOpportunityButton,
                  userModulesRoles.opptracker,
                  userModulesRoles
                )
              }
              label="Add Iteration"
              style={{
                backgroundColor: '#522D6E',
                marginLeft: '1rem',
                border: '1px solid #522D6E',
                fontSize: '0.8rem',
                padding: '0.5rem 0.75rem',
              }}
              onClick={handleOpenAddIterationModal}
            />
            <a
              className={styles.moreIterations}
              onClick={() => setIsSeeMoreIterationModalOpen(true)}
              style={{
                pointerEvents: isIterationActive ? 'auto' : 'none',
                opacity: isIterationActive ? '1' : '0.5',
              }}
            >
              See more iterations
            </a>
          </div>
        ) : (
          <Skeleton height="2.5rem" width="20rem" style={{ marginLeft: '2rem' }} />
        )}
      </>
    );
  };

  const [activeIndex, setActiveIndex] = useState([
    { ID: 0, name: 'CONCEPTS', active: false },
    { ID: 1, name: 'BASIC DETAILS', active: false },
    { ID: 2, name: 'PARTNERS', active: false },
    { ID: 3, name: 'FINANCIALS', active: false },
    { ID: 4, name: 'NC DETAILS', active: false },
  ]);

  const assigneeOptionTemplate = (option: User | null) => {
    return (
      <>
        {option ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              position: 'relative',
            }}
          >
            <Avatar
              title={option.Name}
              id="profileAvatar"
              shape="circle"
              label={option.Name && getInitials(option?.Name)}
              style={{
                backgroundColor: getBackgroundColor(option.Name[0]),
                color: '#522D6E',
                marginRight: '0.5rem',
              }}
            />
            <div>{truncateName(option.Name, 13)}</div>
          </div>
        ) : (
          <span>Unassigned</span>
        )}
      </>
    );
  };

  const [isInputFocused, setIsInputFocused] = useState(false);

  return (
    <Dialog
      maximizable
      headerClassName={styles.modalHeader}
      contentClassName={styles.modalContent}
      header={() => header()}
      style={{ width: '95vw', minHeight: '98vh', margin: 'auto' }}
      visible={visible}
      onHide={handleEditOpportunityModalClose}
      blockScroll
      draggable={false}
      resizable={false}
    >
      {editingOpportunity && comments && concepts?.Concept_info ? (
        <>
          {(iterationType === 'NC' || iterationType === 'MAS') && (
            <div className={styles.mainContent}>
              <div className={styles.descComments}>
                <div className={styles.title}>
                  <InputText
                    value={moleculeName || ''}
                    placeholder="Drug Molecule"
                    className={styles.titleInput}
                    disabled
                  />
                  <button className="text-gray-400" style={{ cursor: 'pointer' }}></button>
                </div>
                <div className={styles.personnel}>
                  <div className={styles.dropdown}>
                    <label htmlFor="analyst">ANALYST</label>
                    <Dropdown
                      valueTemplate={assigneeOptionTemplate}
                      itemTemplate={assigneeOptionTemplate}
                      options={dropdownDetails?.[iterationType].User}
                      optionLabel="Name"
                      optionValue="ID"
                      value={editingOpportunity.AnalystID || ''}
                      onChange={e =>
                        setEditingOpportunity({
                          ...editingOpportunity,
                          AnalystID: e.value,
                        })
                      }
                      style={{
                        width: '17rem',
                        height: '3.6rem',
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        border: '1px solid #c2c2c2',
                        borderRadius: '5px',
                      }}
                      placeholder="Select Analyst"
                      className={styles.selectDropdown}
                      disabled={!isIterationActive}
                    />
                  </div>
                  <div className={styles.dropdown}>
                    <label htmlFor="accountManager">ACCOUNT MANAGER</label>
                    <Dropdown
                      valueTemplate={assigneeOptionTemplate}
                      itemTemplate={assigneeOptionTemplate}
                      options={dropdownDetails?.[iterationType].User}
                      optionLabel="Name"
                      optionValue="ID"
                      value={editingOpportunity.AccountManagerID || ''}
                      onChange={e =>
                        setEditingOpportunity({
                          ...editingOpportunity,
                          AccountManagerID: e.value,
                        })
                      }
                      style={{
                        width: '17rem',
                        height: '3.6rem',
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        border: '1px solid #c2c2c2',
                        borderRadius: '5px',
                      }}
                      placeholder="Select Account Manager"
                      className={styles.selectDropdown}
                      disabled={!isIterationActive}
                    />
                  </div>
                </div>
                <div>
                  <ReactQuill
                    value={editingOpportunity.Description || ''}
                    onChange={content => {
                      setEditingOpportunity({
                        ...editingOpportunity,
                        Description: content,
                      });
                    }}
                    placeholder="Add Description"
                    style={{
                      height: '10rem',
                      pointerEvents: !isIterationActive ? 'none' : 'auto',
                    }}
                    modules={{
                      toolbar: [
                        ['bold', 'italic', 'underline'],
                        ['link'],
                        [{ list: 'ordered' }, { list: 'bullet' }],
                        ['clean'],
                      ],
                      maxlength: { maxLength: 2993 },
                    }}
                  />
                </div>
                <div
                  style={{
                    marginTop: '2.5rem',
                    display: 'flex',
                    justifyContent: 'flex-end',
                  }}
                >
                  <span style={{ paddingTop: '10px', fontSize: '12px' }}>
                    {editingOpportunity.Description
                      ? editingOpportunity.Description === '<p><br></p>'
                        ? 0
                        : editingOpportunity.Description.length
                      : 0}
                    /3000
                  </span>
                </div>
                <div className={styles.comments}>
                  <TabView
                    scrollable
                    className={styles.customTab}
                    activeIndex={currentCommentsIndex}
                    onTabChange={e => setCurrentCommentsIndex(e.index)}
                    key={tabKey}
                  >
                    <TabPanel
                      header="Comments"
                      contentClassName={styles.panelContainer}
                      headerClassName={`${
                        currentCommentsIndex === 0 ? styles.selectedTab : styles.unselectedTab
                      }`}
                    >
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          position: 'relative',
                        }}
                      >
                        <ReactQuill
                          value={comment || ''}
                          onChange={content => {
                            handleCommentChange(content);
                            handleEditorChange(content);
                          }}
                          placeholder="Add Comments..."
                          style={{
                            height: '5rem',
                            pointerEvents: !isIterationActive ? 'none' : 'auto',
                          }}
                          modules={{
                            toolbar: [
                              ['bold', 'italic', 'underline', 'strike', 'blockquote'],
                              [{ header: [1, 2, false] }],
                              [{ list: 'ordered' }, { list: 'bullet' }],
                              ['link', 'image'],
                              ['clean'],
                            ],
                            maxlength: { maxLength: 993 },
                          }}
                        />
                        {/* {comment ? comment.length : 0} */}
                        <div
                          style={{
                            marginTop: '2.5rem',
                            display: 'flex',
                            justifyContent: 'flex-end',
                          }}
                        >
                          <span style={{ paddingTop: '10px', fontSize: '12px' }}>
                            {charCount}/1000
                          </span>
                        </div>

                        <Button
                          label="Add"
                          style={{
                            position: 'absolute',
                            top: '0.3rem',
                            right: '0.3rem',
                            backgroundColor: '#029999',
                            border: '1px solid #029999',
                            alignSelf: 'flex-end',
                            padding: '0.35rem 0.75rem',
                          }}
                          disabled={
                            comment === '' ||
                            !isIterationActive ||
                            isCommentsDataLoading ||
                            !checkIfFunctionalityEnabled(
                              bdTrackerAllowedRoles.commentAddButton,
                              userModulesRoles.opptracker,
                              userModulesRoles
                            )
                          }
                          loading={isCommentSaving}
                          onClick={() => {
                            const payload: CommentRequestPayload = {
                              OpportunityIterationID: editingOpportunity.OpportunityIterationsID!,
                              StageID: editingOpportunity.StageID!,
                              StatusID: editingOpportunity.StatusID!,
                              Comments: comment,
                              IsActive: true,
                            };
                            saveComments(payload);
                            setComment('');
                          }}
                        />
                      </div>
                      {/* Render existing comments */}

                      {/* COMMENTS CONTAINER */}
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '2rem',
                          height: '100px',
                          padding: '5px',
                        }}
                      >
                        {comments?.map((comment, index) => (
                          <div
                            key={index}
                            style={{
                              display: 'flex',
                              width: '100%',
                              gap: '0.3rem',
                              borderTop: '1px solid #c3bbbb',
                              paddingTop: '1rem',
                            }}
                          >
                            <div
                              style={{
                                display: 'flex',
                                gap: '0.75rem',
                                alignItems: 'start',
                              }}
                            >
                              <Avatar
                                title={comment.LastModifiedBy}
                                id="profileAvatar"
                                shape="circle"
                                label={
                                  comment.LastModifiedBy && getInitials(comment.LastModifiedBy)
                                }
                                style={{
                                  cursor: 'pointer',
                                  backgroundColor: getBackgroundColor(comment.LastModifiedBy[0]),
                                  color: '#522D6E',
                                  marginRight: '0.5rem',
                                }}
                              />
                            </div>
                            <div
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                                flexGrow: 1,
                                gap: '0.4rem',
                                boxShadow:
                                  'rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px',
                              }}
                            >
                              <div
                                style={{ padding: '0 0.5rem' }}
                                className="comment-content"
                                dangerouslySetInnerHTML={{
                                  __html: comment.Comments,
                                }}
                              />
                              <div
                                style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'end',
                                  padding: '0 0.5rem',
                                }}
                              >
                                <div>
                                  {/* <span style={{ color: "red", cursor: "pointer", fontWeight: "500", fontSize: "0.7rem" }}>Delete</span> */}
                                </div>
                                <p style={{ margin: 0, fontSize: '0.75rem' }}>
                                  commented on{' '}
                                  {moment(comment.LastModifiedDate).format('MMMM Do YYYY, h:mm A')}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </TabPanel>
                    <TabPanel
                      header="History"
                      contentClassName={styles.panelContainer}
                      headerClassName={`${
                        currentCommentsIndex === 1 ? styles.selectedTab : styles.unselectedTab
                      }`}
                    >
                      <HistoryTab iterationId={opportunityIterationID} />
                    </TabPanel>
                  </TabView>
                </div>
              </div>
              <div className={styles.details}>
                <div className={styles.status}>
                  <div style={{ display: 'flex', gap: '1.5rem' }}>
                    <div className={styles.dropdown}>
                      <label htmlFor="status">STATUS</label>
                      <Dropdown
                        value={editingOpportunity.StatusID || ''}
                        onChange={e => {
                          setEditingOpportunity({
                            ...editingOpportunity,
                            StatusID: e.value,
                          });
                        }}
                        options={getStatus(
                          editingOpportunity.StageID || 0,
                          dropdownDetails?.[iterationType].Status || []
                        )}
                        optionLabel="Status"
                        optionValue="ID"
                        className={styles.selectDropdown}
                        style={{
                          width: '9rem',
                          backgroundColor:
                            (editingOpportunity.StatusID === 1 &&
                              editingOpportunity.StageID !== 8) ||
                            (editingOpportunity.StageID === 8 && editingOpportunity.StatusID === 5)
                              ? '#ffcccc'
                              : editingOpportunity.StatusID === 2 ||
                                  (editingOpportunity.StageID === 8 &&
                                    editingOpportunity.StatusID === 1)
                                ? '#fff4cc'
                                : editingOpportunity.StatusID === 3 ||
                                    editingOpportunity.StatusID === 4
                                  ? '#d4f8cc'
                                  : 'white',
                          border:
                            (editingOpportunity.StatusID === 1 &&
                              editingOpportunity.StageID !== 8) ||
                            (editingOpportunity.StageID === 8 && editingOpportunity.StatusID === 5)
                              ? '1px solid #a80000'
                              : editingOpportunity.StatusID === 2 ||
                                  (editingOpportunity.StageID === 8 &&
                                    editingOpportunity.StatusID === 1)
                                ? '1px solid #b38b00'
                                : editingOpportunity.StatusID === 3 ||
                                    editingOpportunity.StatusID === 4
                                  ? '1px solid #006400'
                                  : '1px solid transparent',
                        }}
                        pt={{
                          input: {
                            style: {
                              color:
                                (editingOpportunity.StatusID === 1 &&
                                  editingOpportunity.StageID !== 8) ||
                                (editingOpportunity.StageID === 8 &&
                                  editingOpportunity.StatusID === 5)
                                  ? '#a80000'
                                  : editingOpportunity.StatusID === 2 ||
                                      (editingOpportunity.StageID === 8 &&
                                        editingOpportunity.StatusID === 1)
                                    ? '#b38b00'
                                    : editingOpportunity.StatusID === 3 ||
                                        editingOpportunity.StatusID === 4
                                      ? '#006400'
                                      : 'black',
                            },
                          },
                          trigger: {
                            style: {
                              color:
                                (editingOpportunity.StatusID === 1 &&
                                  editingOpportunity.StageID !== 8) ||
                                (editingOpportunity.StageID === 8 &&
                                  editingOpportunity.StatusID === 5)
                                  ? '#a80000'
                                  : editingOpportunity.StatusID === 2 ||
                                      (editingOpportunity.StageID === 8 &&
                                        editingOpportunity.StatusID === 1)
                                    ? '#b38b00'
                                    : editingOpportunity.StatusID === 3 ||
                                        editingOpportunity.StatusID === 4
                                      ? '#006400'
                                      : 'black',
                            },
                          },
                        }}
                        disabled={!isIterationActive}
                      />
                    </div>
                    <div className={styles.dropdown}>
                      <label htmlFor="stage">STAGE</label>
                      <Dropdown
                        value={editingOpportunity.StageID || ''}
                        onChange={e => {
                          if (initialOpportunity?.StageID != null && isValidStatusMove(initialOpportunity.StageID, e.value)) {
                            setEditingOpportunity({
                              ...editingOpportunity,
                              StageID: e.value,
                              StatusID: 1,
                            });
                          } else {
                            toast.error('Invalid move, requires admin role', {
                              position: 'bottom-right',
                              autoClose: 5000,
                            });
                          }
                        }}
                        disabled={!isIterationActive}
                        options={dropdownDetails?.[iterationType].Stage}
                        optionLabel="Stage"
                        optionValue="ID"
                        className={styles.selectDropdown}
                        style={{
                          width: '13rem',
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'center',
                          border: '1px solid #c2c2c2',
                          borderRadius: '5px',
                        }}
                      />
                    </div>
                  </div>
                  <Button
                    label="Save"
                    icon="pi pi-save"
                    iconPos="right"
                    onClick={() => {
                      if (editingOpportunity) handleSaveTask(editingOpportunity);
                      setActiveIndex([
                        { ID: 0, name: 'CONCEPTS', active: false },
                        { ID: 1, name: 'BASIC DETAILS', active: false },
                        { ID: 2, name: 'PARTNERS', active: false },
                        { ID: 3, name: 'FINANCIALS', active: false },
                        { ID: 4, name: 'NC DETAILS', active: false },
                      ]);
                    }}
                    style={{ marginLeft: 'auto', border: 'none' }}
                    disabled={
                      !isIterationActive ||
                      handleCheckIfSaveButtonIsDisabled() ||
                      !checkIfFunctionalityEnabled(
                        bdTrackerAllowedRoles.editOpportunitySaveButton,
                        userModulesRoles.opptracker,
                        userModulesRoles
                      )
                    }
                    loading={isOpportunityDetailsSaving}
                  />
                </div>
                <div className={styles.accordionSection}>
                  <Accordion
                    activeIndex={openAccordionIndexes}
                    key={accordionKeyIndex}
                    multiple
                    onTabOpen={e =>
                      setActiveIndex(prev =>
                        prev.map(itm => (itm.ID === e.index ? { ...itm, active: true } : itm))
                      )
                    }
                    onTabClose={e =>
                      setActiveIndex(prev =>
                        prev.map(itm => (itm.ID === e.index ? { ...itm, active: false } : itm))
                      )
                    }
                  >
                    <AccordionTab
                      header={
                        <div className={styles.accordianHeading}>
                          <span>CONCEPTS</span>
                          <img src={conceptIcon} alt="conceptIcon" />
                          <span className={styles.accordianOptions}>Package sizes</span>
                        </div>
                      }
                      className={
                        activeIndex.find(item => item.name === 'CONCEPTS')?.active
                          ? styles.accordionTab
                          : styles.inactiveAccordionTab
                      }
                    >
                      <div className={styles.concepts}>
                        {(concepts?.Concept_info || []).length === 0 && (
                          <div
                            style={{
                              margin: '0 auto',
                              padding: '1rem',
                              width: '100%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '1rem',
                            }}
                          >
                            <MultiSelect
                              selectAllLabel="Select All"
                              value={selectedConcepts}
                              options={filterExistingConcepts()}
                              optionLabel="Strength"
                              itemTemplate={option => (
                                <div
                                  style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                  }}
                                >
                                  <h2
                                    style={{
                                      margin: 0,
                                      fontSize: '1rem',
                                      fontWeight: 'bold',
                                    }}
                                  >
                                    {option.Strength}
                                  </h2>
                                  <p
                                    style={{
                                      margin: 0,
                                      fontSize: '0.875rem',
                                      color: '#666',
                                    }}
                                  >
                                    {option.Concept}
                                  </p>
                                </div>
                              )}
                              onChange={e => setSelectedConcepts(e.value)}
                              placeholder="Add a Concept"
                              style={{ textAlign: 'center', width: '35rem' }}
                              disabled={!isIterationActive}
                            />
                            <Button
                              className={styles.confirmButton}
                              onClick={async () => {
                                if (selectedConcepts.length > 0) {
                                  const payloads = createPayloads();
                                  try {
                                    await saveConcept(payloads);
                                    await getConcepts(opportunityIterationID!);
                                    setSelectedConcepts([]);
                                    toast.success('Concepts saved successfully!');
                                  } catch (error) {
                                    console.error('Error saving concepts:', error);
                                    toast.error('Error saving concepts. Please try again.');
                                  }
                                } else {
                                  toast.info('Please select at least one concept.');
                                }
                              }}
                              disabled={selectedConcepts.length === 0 || !isIterationActive}
                              style={{
                                display: 'flex',
                                justifyContent: 'center',
                                padding: '0.5rem',
                                fontSize: '12px',
                                maxWidth: '25rem',
                              }}
                            >
                              Confirm
                            </Button>
                          </div>
                        )}

                        {/* Tab View */}
                        {(concepts?.Concept_info || []).length > 0 && (
                          <TabView
                            scrollable
                            className={styles.customTab}
                            activeIndex={currentConceptsIndex}
                            onTabChange={e => setCurrentConceptsIndex(e.index)}
                          >
                            {concepts?.Concept_info.map((concept, index) => (
                              <TabPanel
                                key={index}
                                header={concept.Strength}
                                style={{ position: 'relative' }}
                                headerClassName={`${
                                  currentConceptsIndex === index
                                    ? styles.selectedTab
                                    : styles.unselectedTab
                                }`}
                              >
                                {/* Tab Panel Content */}
                                {isConceptSaving && (
                                  <div
                                    style={{
                                      position: 'absolute',
                                      top: 0,
                                      left: 0,
                                      right: 0,
                                      bottom: 0,
                                      backgroundColor: 'rgba(255, 255, 255, 0.7)',
                                      display: 'flex',
                                      justifyContent: 'center',
                                      alignItems: 'center',
                                      zIndex: 10,
                                    }}
                                  >
                                    <ProgressSpinner
                                      style={{ width: '30px', height: '30px' }}
                                      strokeWidth="8"
                                      fill="var(--surface-ground)"
                                      animationDuration=".5s"
                                    />
                                  </div>
                                )}
                                <div
                                  className="p-grid"
                                  style={{
                                    padding: '0.5rem',
                                    position: 'relative',
                                    display: 'flex',
                                    alignItems: 'center',
                                    flexWrap: 'wrap',
                                    gap: '0.5rem',
                                  }}
                                >
                                  {(concept?.PackSize || [])
                                    .filter(PackSize => PackSize !== null)
                                    .sort()
                                    .map((PackSize, index) => (
                                      <div
                                        key={index}
                                        style={{
                                          padding: '0.25rem 0.75rem',
                                          background: '#f6edff',
                                          border: '1px solid #E5E7EB',
                                          display: 'inline-block',
                                          borderRadius: '4px',
                                        }}
                                      >
                                        {PackSize}
                                      </div>
                                    ))}
                                  {editIndex === index && (
                                    <div
                                      style={{
                                        padding: '0.25rem 0.75rem',
                                        background: '#f6edff',
                                        border: '1px solid #E5E7EB',
                                        display: 'inline-block',
                                        borderRadius: '4px',
                                        maxWidth: 'auto',
                                      }}
                                    >
                                      <InputText
                                        value={newPackageSize}
                                        onBlur={() => {
                                          const trimmed = newPackageSize ?? '';
                                          if (!trimmed) {
                                            toast.warning('Please provide a pack size.');
                                          } else if (
                                            !/^\d+(\.\d+)?( ?(GM|ML))? ?([Xx] ?\d+(\.\d+)?( ?(GM|ML))?)?$/i.test(
                                              trimmed
                                            )
                                          ) {
                                            toast.warning(
                                              'Please enter a valid pack size (e.g. 2.00, 1.00 X 3.50, or 1.00 X 3.50 GM).'
                                            );
                                          } else {
                                            handleSaveConcept(
                                              editingOpportunity.OpportunityIterationsID!,
                                              concept.Strength,
                                              true,
                                              trimmed
                                            );
                                          }
                                        }}
                                        onChange={e => {
                                          // Accept structurally valid partial input, including optional GM/ML units:
                                          //   "2", "2.00", "1.00 X 3.50", "1.00 X 3.50 GM",
                                          //   "1.00 ML X 3.50", "2.00GM X 3.50", "2.00 X 3.50ML"
                                          // Units (GM/ML) may appear with or without a space, after either number.
                                          const value = e.target.value;
                                          const partiallyValid =
                                            /^(\d+\.?\d*( ?(GM|ML|G|M))? ?([Xx]( ?(\d*\.?\d*( ?(GM|ML|G|M))? ?)?)?)?)?$/i.test(
                                              value
                                            );
                                          if (value.length <= 25 && partiallyValid) {
                                            setNewPackageSize(value);
                                          }
                                        }}
                                        style={{
                                          width: `${Math.max(7, (newPackageSize?.length ?? 0) + 2)}ch`,
                                          minWidth: '7ch',
                                          maxWidth: '20ch',
                                        }}
                                        className="no-input-padding"
                                        placeholder="e.g. 2.00 or 1.00 X 3.50 GM"
                                      />
                                    </div>
                                  )}
                                  <div
                                    style={{
                                      textDecoration: 'none',
                                      cursor: 'pointer',
                                      pointerEvents: isIterationActive ? 'auto' : 'none',
                                      opacity: isIterationActive ? '0.75' : '0.5',
                                    }}
                                    onMouseOver={e =>
                                      ((e.target as HTMLSpanElement).style.textDecoration =
                                        'underline')
                                    }
                                    onMouseOut={e =>
                                      ((e.target as HTMLSpanElement).style.textDecoration = 'none')
                                    }
                                    onClick={() => setEditIndex(index)}
                                  >
                                    <strong style={{ opacity: 0.75 }}>+ Add package size</strong>
                                  </div>
                                </div>
                              </TabPanel>
                            ))}
                            {filterExistingConcepts().length !== 0 && (
                              <TabPanel header="+" headerClassName={styles.unselectedTab}>
                                <div
                                  style={{
                                    margin: '0 auto',
                                    padding: '1rem',
                                    width: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                  }}
                                >
                                  <div
                                    style={{
                                      margin: '0 auto',
                                      padding: '1rem',
                                      width: '100%',
                                      display: 'flex',
                                      justifyContent: 'center',
                                      gap: '1rem',
                                    }}
                                  >
                                    <MultiSelect
                                      selectAllLabel="Select All"
                                      className="concepts-suggestion"
                                      value={selectedConcepts}
                                      options={filterExistingConcepts()}
                                      optionLabel="Strength"
                                      itemTemplate={option => (
                                        <div
                                          style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                          }}
                                        >
                                          <h2
                                            style={{
                                              margin: 0,
                                              fontSize: '1rem',
                                              fontWeight: 'bold',
                                            }}
                                          >
                                            {option.Strength}
                                          </h2>
                                          <p
                                            style={{
                                              margin: 0,
                                              fontSize: '0.875rem',
                                              color: '#666',
                                            }}
                                          >
                                            {option.Concept}
                                          </p>
                                        </div>
                                      )}
                                      onChange={e => setSelectedConcepts(e.value)}
                                      placeholder="Add a Concept"
                                      style={{
                                        textAlign: 'center',
                                        width: '25rem',
                                      }}
                                      disabled={!isIterationActive}
                                    />
                                    <Button
                                      className={styles.confirmButton}
                                      onClick={async () => {
                                        if (selectedConcepts.length > 0) {
                                          const payloads = createPayloads();
                                          try {
                                            await saveConcept(payloads);
                                            await getConcepts(opportunityIterationID!);
                                            setSelectedConcepts([]);
                                            toast.success('Concepts saved successfully!');
                                          } catch (error) {
                                            console.error('Error saving concepts:', error);
                                            toast.error('Error saving concepts. Please try again.');
                                          }
                                        } else {
                                          toast.info('Please select at least one concept.');
                                        }
                                      }}
                                      disabled={selectedConcepts.length === 0 || !isIterationActive}
                                      loading={isConceptSaving || isConceptsDataLoading}
                                      style={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        padding: '0.5rem',
                                        fontSize: '12px',
                                        maxWidth: '35rem',
                                      }}
                                    >
                                      Confirm
                                    </Button>
                                  </div>
                                </div>
                              </TabPanel>
                            )}
                          </TabView>
                        )}
                      </div>

                      {isPortfolioOpportunity && (
                        <>
                          {isIterationNdcPacksizeStrengthsLoading ? (
                            <Skeleton height="6rem" style={{ width: '100%', margin: '0 1rem' }} />
                          ) : (
                            <div className={styles.ndcPacksizeStrengthContainer}>
                              <div className={styles.heading}>Strength-NDC-Packsize</div>
                              <table>
                                <thead>
                                  <th>Strength</th>
                                  <th>NDC</th>
                                  <th>Packsize</th>
                                </thead>
                                <tbody>
                                  {[...iterationNdcPacksizeStrengths]
                                    .sort((a, b) => {
                                      // First sort by Strength
                                      const strengthCompare = (a.Strength || '').localeCompare(
                                        b.Strength || ''
                                      );
                                      if (strengthCompare !== 0) return strengthCompare;

                                      // If Strengths are equal, sort by PackSize
                                      const aPackSize = a.PackSize || '';
                                      const bPackSize = b.PackSize || '';
                                      const packSizeCompare = aPackSize.localeCompare(bPackSize);
                                      if (packSizeCompare !== 0) return packSizeCompare;

                                      // If PackSizes are equal, sort by NDC
                                      const aNDC = a.NDC || '';
                                      const bNDC = b.NDC || '';
                                      return aNDC.localeCompare(bNDC);
                                    })
                                    .map((val, index) => {
                                      return (
                                        <tr key={index}>
                                          <td>{val.Strength ? val.Strength : '-'}</td>
                                          <td>{val.NDC ? val.NDC : '-'}</td>
                                          <td>{val.PackSize ? val.PackSize : '-'}</td>
                                        </tr>
                                      );
                                    })}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </>
                      )}
                    </AccordionTab>
                    <AccordionTab
                      className={
                        activeIndex.filter(item => item.name === 'BASIC DETAILS')[0].active
                          ? styles.accordionTab
                          : styles.inactiveAccordionTab
                      }
                      header={
                        <div className={styles.accordianHeading}>
                          <span>BASIC DETAILS</span>
                          <img src={basicDetaislIcon} alt="basic details" />
                          <span className={styles.accordianOptions}>Source, Strategy, Agency</span>
                        </div>
                      }
                    >
                      <div className={styles.basicDetails}>
                        <div className="p-grid">
                          <div className={styles.dropdown}>
                            <label htmlFor="source">SOURCE</label>
                            <Dropdown
                              options={dropdownDetails?.[iterationType].Source}
                              optionLabel="Source"
                              optionValue="ID"
                              value={editingOpportunity.SourceID || ''}
                              onChange={e =>
                                setEditingOpportunity({
                                  ...editingOpportunity,
                                  SourceID: e.value,
                                })
                              }
                              disabled={!isIterationActive || isPortfolioOpportunity}
                              placeholder="Select Source"
                              className={styles.selectDropdown}
                            />
                          </div>
                          <div>
                            <div
                              className={styles.radioButtons}
                              style={{ margin: '2.5rem 0 1.5rem' }}
                            >
                              <div className="p-field p-col-6">
                                <input
                                  type="radio"
                                  name="source"
                                  value="true"
                                  checked={editingOpportunity.NadWaiver === true}
                                  onChange={() =>
                                    setEditingOpportunity({
                                      ...editingOpportunity,
                                      NadWaiver: true,
                                    })
                                  }
                                  disabled={!isIterationActive}
                                />
                                <label htmlFor="source-yes">Yes</label>
                              </div>
                              <div className="p-field p-col-6">
                                <input
                                  type="radio"
                                  name="source"
                                  value="false"
                                  checked={editingOpportunity.NadWaiver === false}
                                  onChange={() =>
                                    setEditingOpportunity({
                                      ...editingOpportunity,
                                      NadWaiver: false,
                                    })
                                  }
                                  disabled={!isIterationActive}
                                />
                                <label htmlFor="source-no">No</label>
                              </div>
                            </div>
                            <Button
                              label="NAD waiver"
                              icon="pi pi-chevron-right"
                              iconPos="right"
                              onClick={handleOpenNADWaiverModal}
                              style={{ background: '#522D6E' }}
                              disabled={!editingOpportunity.NadWaiver}
                            />
                          </div>
                        </div>
                        <div className="p-grid">
                          <div className={styles.dropdown}>
                            <label htmlFor="strategy">STRATEGY</label>
                            <Dropdown
                              options={dropdownDetails?.[iterationType].Strategy}
                              optionLabel="Strategy"
                              optionValue="ID"
                              value={editingOpportunity.StrategyID || ''}
                              onChange={e =>
                                setEditingOpportunity({
                                  ...editingOpportunity,
                                  StrategyID: e.value,
                                })
                              }
                              placeholder="Select Strategy"
                              className={styles.selectDropdown}
                              disabled={!isIterationActive}
                            />
                          </div>
                          <div>
                            <div
                              className={styles.radioButtons}
                              style={{ margin: '2.5rem 0 1.5rem' }}
                            >
                              <div className="p-field p-col-6">
                                <input
                                  type="radio"
                                  name="strategy"
                                  value="Yes"
                                  checked={editingOpportunity.SiteTransfer === true}
                                  onChange={() =>
                                    setEditingOpportunity({
                                      ...editingOpportunity,
                                      SiteTransfer: true,
                                    })
                                  }
                                  disabled={!isIterationActive}
                                />
                                <label htmlFor="strategy-yes">Yes</label>
                              </div>
                              <div className="p-field p-col-6">
                                <input
                                  type="radio"
                                  name="strategy"
                                  value="No"
                                  checked={editingOpportunity.SiteTransfer === false}
                                  onChange={() =>
                                    setEditingOpportunity({
                                      ...editingOpportunity,
                                      SiteTransfer: false,
                                    })
                                  }
                                  disabled={!isIterationActive}
                                />
                                <label htmlFor="strategy-no">No</label>
                              </div>
                            </div>
                            <Button
                              label="Site Transfer"
                              icon="pi pi-chevron-right"
                              iconPos="right"
                              disabled={!editingOpportunity.SiteTransfer}
                              style={{ background: '#522D6E' }}
                              onClick={handleOpenSiteTransferWaiverModal}
                            />
                          </div>
                        </div>
                        <div className="p-grid">
                          <div className={styles.dropdown}>
                            <label htmlFor="agency">AGENCY</label>
                            <MultiSelect
                              selectAllLabel="Select All"
                              options={dropdownDetails?.[iterationType].Agency}
                              optionLabel="SourceName"
                              optionValue="SourceID"
                              value={editingOpportunity.Agency || ''}
                              onChange={e =>
                                setEditingOpportunity({
                                  ...editingOpportunity,
                                  Agency: e.value,
                                })
                              }
                              placeholder="Select Agency"
                              className={styles.selectDropdown}
                              disabled={!isIterationActive}
                            />
                          </div>
                        </div>
                      </div>
                    </AccordionTab>
                    <AccordionTab
                      header={
                        <div className={styles.accordianHeading}>
                          <span>PARTNERS</span>
                          <img src={partnersIcon} alt="parternsIcon" />
                          <span className={styles.accordianOptions}>
                            Primary partner, Backup partner 1, Backup partner 2
                          </span>
                        </div>
                      }
                      className={
                        opportunityErrorDetails.partners.errorDetails.primaryPartner ||
                        opportunityErrorDetails.partners.errorDetails.backupPartner1 ||
                        opportunityErrorDetails.partners.errorDetails.backupPartner2 ||
                        activeIndex.filter(item => item.name === 'PARTNERS')[0].active
                          ? styles.accordionTab
                          : styles.inactiveAccordionTab
                      }
                    >
                      {(opportunityErrorDetails.partners.errorDetails.primaryPartner ||
                        opportunityErrorDetails.partners.errorDetails.backupPartner1 ||
                        opportunityErrorDetails.partners.errorDetails.backupPartner2) && (
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            color: 'red',
                            fontSize: '14px',
                            marginBottom: '-1rem',
                            paddingRight: '1rem',
                          }}
                        >
                          <span>*required</span>
                        </div>
                      )}
                      <div className={styles.partners}>
                        <div className={styles.dropdown}>
                          <label htmlFor="primaryPartner">
                            PRIMARY PARTNER
                            {opportunityErrorDetails.partners.errorDetails.primaryPartner && (
                              <span style={{ color: 'red' }}>*</span>
                            )}
                          </label>
                          <AsyncSelect
                            loadOptions={fetchSuggestionsWithDelay}
                            // onInputChange={handleInputChange}
                            onChange={option => {
                              setEditingOpportunity({
                                ...editingOpportunity,
                                PrimarySupplierID: option?.value || '',
                              });
                            }}
                            defaultOptions
                            placeholder="Search..."
                            value={
                              editingOpportunity.PrimarySupplierID
                                ? {
                                    label: editingOpportunity.PrimarySupplierID,
                                    value: editingOpportunity.PrimarySupplierID,
                                  }
                                : null
                            }
                            isDisabled={!isIterationActive || isPortfolioOpportunity}
                            styles={{
                              menu: provided => ({ ...provided, zIndex: 20 }),
                              control: (base, _state) => ({
                                ...base,
                                background: 'var(--bg-table-row)',
                                border: '1px solid var(--border-primary)',
                                borderRadius: '0.5rem',
                              }),
                            }}
                          />
                        </div>
                        <div className={styles.dropdown}>
                          <label htmlFor="backupPartner1">
                            BACKUP PARTNER 1
                            {opportunityErrorDetails.partners.errorDetails.backupPartner1 && (
                              <span style={{ color: 'red' }}>*</span>
                            )}
                          </label>
                          <AsyncSelect
                            loadOptions={fetchSuggestionsWithDelay}
                            // onInputChange={handleInputChange}
                            onChange={option => {
                              setEditingOpportunity({
                                ...editingOpportunity,
                                BackupSupplierID1: option?.value || '',
                              });
                            }}
                            defaultOptions
                            placeholder="Search..."
                            value={
                              editingOpportunity.BackupSupplierID1
                                ? {
                                    label: editingOpportunity.BackupSupplierID1,
                                    value: editingOpportunity.BackupSupplierID1,
                                  }
                                : null
                            }
                            isDisabled={!isIterationActive}
                            styles={{
                              menu: provided => ({ ...provided, zIndex: 20 }),
                              control: (base, _state) => ({
                                ...base,
                                background: 'var(--bg-table-row)',
                                border: '1px solid var(--border-primary)',
                                borderRadius: '0.5rem',
                              }),
                            }}
                          />
                        </div>
                        <div className={styles.dropdown}>
                          <label htmlFor="backupPartner2">
                            BACKUP PARTNER 2
                            {opportunityErrorDetails.partners.errorDetails.backupPartner2 && (
                              <span style={{ color: 'red' }}>*</span>
                            )}
                          </label>
                          <AsyncSelect
                            loadOptions={fetchSuggestionsWithDelay}
                            // onInputChange={handleInputChange}
                            onChange={option => {
                              setEditingOpportunity({
                                ...editingOpportunity,
                                BackupSupplierID2: option?.value || '',
                              });
                            }}
                            defaultOptions
                            placeholder="Search..."
                            value={
                              editingOpportunity.BackupSupplierID2
                                ? {
                                    label: editingOpportunity.BackupSupplierID2,
                                    value: editingOpportunity.BackupSupplierID2,
                                  }
                                : null
                            }
                            isDisabled={!isIterationActive}
                            styles={{
                              menu: provided => ({ ...provided, zIndex: 20 }),
                              control: (base, _state) => ({
                                ...base,
                                background: 'var(--bg-table-row)',
                                border: '1px solid var(--border-primary)',
                                borderRadius: '0.5rem',
                              }),
                            }}
                          />
                        </div>
                      </div>
                    </AccordionTab>
                    <AccordionTab
                      header={
                        <div className={styles.accordianHeading}>
                          <span>FINANCIALS</span>
                          <img src={financialIcon} alt="financialsIcon" />
                          <span className={styles.accordianOptions}>
                            Estimated annual revenue, Estimated annual gp, Expected launch date
                          </span>
                        </div>
                      }
                      className={
                        activeIndex.filter(item => item.name === 'FINANCIALS')[0].active
                          ? styles.accordionTab
                          : styles.inactiveAccordionTab
                      }
                    >
                      <div className={styles.financials}>
                        <div className="p-grid">
                          <div className={styles.dropdown}>
                            <label
                              htmlFor="estimatedAnnualRevenue"
                              style={isInputFocused ? { color: 'red' } : {}}
                            >
                              ESTIMATED ANNUAL REVENUE
                            </label>
                            <InputText
                              value={
                                editingOpportunity.EstAnnualRevenue
                                  ? `$${formatNumberWithCommas(editingOpportunity.EstAnnualRevenue)}`
                                  : '$0'
                              }
                              onChange={e => {
                                // Remove all non-digit characters except for the dollar sign and commas
                                let numericValue = e.target.value.replace(/[^0-9]/g, '');

                                // Limit to 19 digits maximum
                                if (numericValue.length > 18) {
                                  numericValue = numericValue.slice(0, 18);
                                }

                                // Convert to number and update state
                                const numberValue = numericValue === '' ? '0' : numericValue;
                                setEditingOpportunity({
                                  ...editingOpportunity,
                                  EstAnnualRevenue: numberValue,
                                });
                              }}
                              style={{
                                background: 'var(--bg-table-row)',
                                color: 'var(--text-primary)',
                              }}
                              onFocus={() => setIsInputFocused(true)}
                              onBlur={() => {
                                setIsInputFocused(false);
                              }}
                              className="p-fluid"
                              disabled={!isIterationActive}
                              placeholder="$0"
                            />
                          </div>
                        </div>
                        <div className="p-grid">
                          <div className={styles.dropdown}>
                            <label htmlFor="estimatedAnnualGP">ESTIMATED ANNUAL GP</label>
                            <InputText
                              value={
                                editingOpportunity.EstGrossProfit
                                  ? `$${formatNumberWithCommas(editingOpportunity.EstGrossProfit)}`
                                  : '$0'
                              }
                              onChange={e => {
                                // Remove all non-digit characters except for the dollar sign and commas
                                let numericValue = e.target.value.replace(/[^0-9]/g, '');

                                // Limit to 19 digits maximum
                                if (numericValue.length > 18) {
                                  numericValue = numericValue.slice(0, 18);
                                }

                                // Convert to number and update state
                                const numberValue = numericValue === '' ? '0' : numericValue;
                                setEditingOpportunity({
                                  ...editingOpportunity,
                                  EstGrossProfit: numberValue,
                                });
                              }}
                              style={{
                                background: 'var(--bg-table-row)',
                                color: 'var(--text-primary)',
                              }}
                              className="p-fluid"
                              disabled={!isIterationActive}
                              placeholder="$0"
                            />
                          </div>
                        </div>
                        <div className="p-grid">
                          <div className={styles.dropdown}>
                            <label htmlFor="expectedLaunchDate">EXPECTED LAUNCH DATE</label>
                            <Calendar
                              view="date"
                              dateFormat="mm/dd/yy"
                              onChange={e => {
                                const formattedDate = DateConversionUtility.convertLocalToDBDateString(
                                  e.value as Date
                                );
                                setEditingOpportunity({
                                  ...editingOpportunity,
                                  LaunchDate: formattedDate as unknown as Date,
                                });
                              }}
                              value={DateConversionUtility.convertDBDateToLocal(
                                editingOpportunity?.LaunchDate
                              )}
                              showIcon
                              disabled={!isIterationActive}
                              pt={{
                                input: {
                                  root: {
                                    style: {
                                      background: 'var(--bg-table-row)',
                                      color: 'var(--text-primary)',
                                    },
                                  },
                                },
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    </AccordionTab>

                    {iterationType === 'NC' && (
                      <AccordionTab
                        header={
                          <div className={styles.accordianHeading}>
                            <span>NC DETAILS</span>
                            <img src={ncdetailsIcon} alt="ncDetailsIcon" />
                            <span className={styles.accordianOptions}>
                              NC bid status, Solicitation number, Submission due date, Awarding
                              agency
                            </span>
                          </div>
                        }
                        className={
                          opportunityErrorDetails.ncDetails.isError ||
                          activeIndex.filter(item => item.name === 'NC DETAILS')[0].active
                            ? styles.accordionTab
                            : styles.inactiveAccordionTab
                        }
                      >
                        {opportunityErrorDetails.ncDetails.isError && (
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'flex-end',
                              color: 'red',
                              fontSize: '14px',
                              marginBottom: '-1rem',
                              paddingRight: '1rem',
                            }}
                          >
                            <span>*required</span>
                          </div>
                        )}
                        <div className={styles.ncDetails}>
                          <div className={styles.row}>
                            <div className={styles.dropdown}>
                              <label htmlFor="ncBidStatus">NC BID STATUS</label>
                              <Dropdown
                                options={dropdownDetails?.NC.NCBidStatus}
                                optionLabel="NCBidStatus"
                                optionValue="ID"
                                value={editingOpportunity.NCBidStatusID || ''}
                                onChange={e =>
                                  setEditingOpportunity({
                                    ...editingOpportunity,
                                    NCBidStatusID: e.value,
                                  })
                                }
                                placeholder="Select NG Bid Status"
                                className={styles.selectDropdown}
                                disabled={!isIterationActive}
                              />
                            </div>
                            <div className={styles.dropdown}>
                              <label
                                htmlFor="solicitationNumber"
                                style={{
                                  opacity: editingOpportunity.NCBidStatusID === 1 ? 0.5 : 1,
                                }}
                              >
                                SOLICITATION NUMBER
                                {opportunityErrorDetails.ncDetails.errorDetails
                                  .solicitationNumber && <span style={{ color: 'red' }}>*</span>}
                              </label>
                              <InputText
                                value={editingOpportunity.SolicitationNumber}
                                onChange={e =>
                                  setEditingOpportunity({
                                    ...editingOpportunity,
                                    SolicitationNumber: e.target
                                      .value!.toLocaleUpperCase()
                                      .replace(/[^A-Z0-9]/g, ''),
                                  })
                                }
                                className={`p-fluid ${
                                  opportunityErrorDetails.ncDetails.errorDetails
                                    .solicitationNumber && 'error-input-box'
                                }`}
                                disabled={
                                  editingOpportunity.NCBidStatusID !== 2 || !isIterationActive
                                }
                                style={{
                                  background: 'var(--bg-table-row)',
                                  color: 'var(--text-primary)',
                                }}
                              />
                            </div>
                            <div className={styles.dropdown}>
                              <label
                                htmlFor="submissionDueDate"
                                style={{
                                  opacity: editingOpportunity.NCBidStatusID === 1 ? 0.5 : 1,
                                }}
                              >
                                SUBMISSION DUE DATE
                                {opportunityErrorDetails.ncDetails.errorDetails
                                  .submissionDueDate && <span style={{ color: 'red' }}>*</span>}
                              </label>
                              <Calendar
                                view="date"
                                dateFormat="mm/dd/yy"
                                onChange={e => {
                                  const formattedDate = DateConversionUtility.convertLocalToDBDateString(
                                    e.value as Date
                                  );
                                  setEditingOpportunity({
                                    ...editingOpportunity,
                                    SubmissionDueDate: formattedDate as unknown as Date,
                                  });
                                }}
                                value={DateConversionUtility.convertDBDateToLocal(
                                  editingOpportunity?.SubmissionDueDate
                                )}
                                showIcon
                                disabled={
                                  editingOpportunity.NCBidStatusID !== 2 || !isIterationActive
                                }
                                inputStyle={
                                  opportunityErrorDetails.ncDetails.errorDetails.submissionDueDate
                                    ? { border: 'none' }
                                    : {}
                                }
                                className={`${
                                  opportunityErrorDetails.ncDetails.errorDetails
                                    .submissionDueDate && 'error-input-box'
                                }`}
                                pt={{
                                  input: {
                                    root: {
                                      style: {
                                        background: 'var(--bg-table-row)',
                                        color: 'var(--text-primary)',
                                      },
                                    },
                                  },
                                }}
                              />
                            </div>
                          </div>
                          <div className={styles.row}>
                            <div className={styles.dropdown}>
                              <label htmlFor="awardingAgency">AWARDING AGENCY</label>
                              <div className="p-field p-col-12">
                                <Dropdown
                                  options={dropdownDetails?.NC.AwardingAgency}
                                  value={editingOpportunity.AwardingAgencyID || ''}
                                  optionLabel="AwardingAgency"
                                  optionValue="ID"
                                  onChange={e =>
                                    setEditingOpportunity({
                                      ...editingOpportunity,
                                      AwardingAgencyID: e.value,
                                    })
                                  }
                                  placeholder="Select Awarding Agency"
                                  className={styles.selectDropdown}
                                  disabled={!isIterationActive}
                                />
                              </div>
                            </div>
                          </div>
                          <div className={styles.row}>
                            <div className={styles.dropdown}>
                              <label htmlFor="reverseAuction">REVERSE AUCTION</label>
                              <div className={styles.radioButtons}>
                                <div className="p-field p-col-6">
                                  <input
                                    type="radio"
                                    name="auction"
                                    value="Yes"
                                    checked={editingOpportunity.ReverseAction === true}
                                    onChange={() =>
                                      setEditingOpportunity({
                                        ...editingOpportunity,
                                        ReverseAction: true,
                                      })
                                    }
                                    disabled={!isIterationActive}
                                  />
                                  <label htmlFor="auction-yes">Yes</label>
                                </div>
                                <div className="p-field p-col-6">
                                  <input
                                    type="radio"
                                    name="auction"
                                    value="No"
                                    checked={!editingOpportunity.ReverseAction}
                                    onChange={() =>
                                      setEditingOpportunity({
                                        ...editingOpportunity,
                                        ReverseAction: false,
                                      })
                                    }
                                    disabled={!isIterationActive}
                                  />
                                  <label htmlFor="auction-no">No</label>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className={styles.row}>
                            <div className={styles.dropdown}>
                              <label htmlFor="negotiation">NEGOTIATION</label>
                              <div className={styles.radioButtons}>
                                <div className="p-field p-col-6">
                                  <input
                                    type="radio"
                                    name="negotiation"
                                    value="Yes"
                                    checked={editingOpportunity?.Negotiation === true}
                                    onChange={() =>
                                      setEditingOpportunity({
                                        ...editingOpportunity,
                                        Negotiation: true,
                                      })
                                    }
                                    disabled={!isIterationActive}
                                  />
                                  <label htmlFor="negotiation-yes">Yes</label>
                                </div>
                                <div className="p-field p-col-6">
                                  <input
                                    type="radio"
                                    name="negotiation"
                                    value="No"
                                    checked={!editingOpportunity?.Negotiation}
                                    onChange={() =>
                                      setEditingOpportunity({
                                        ...editingOpportunity,
                                        Negotiation: false,
                                      })
                                    }
                                    disabled={!isIterationActive}
                                  />
                                  <label htmlFor="negotiation-no">No</label>
                                </div>
                              </div>
                            </div>
                            <div className={styles.dropdown}>
                              <label htmlFor="negotiationsDate">
                                NEGOTIATIONS DATE
                                {opportunityErrorDetails.ncDetails.errorDetails.negotiationDate && (
                                  <span style={{ color: 'red' }}>*</span>
                                )}
                              </label>
                              <Calendar
                                view="date"
                                dateFormat="mm/dd/yy"
                                onChange={e => {
                                  const formattedDate = DateConversionUtility.convertLocalToDBDateString(
                                    e.value as Date
                                  );
                                  setEditingOpportunity({
                                    ...editingOpportunity,
                                    NegotiationDate: formattedDate as unknown as Date,
                                  });
                                }}
                                value={DateConversionUtility.convertDBDateToLocal(
                                  editingOpportunity?.NegotiationDate
                                )}
                                disabled={!editingOpportunity.Negotiation || !isIterationActive}
                                showIcon
                                inputStyle={
                                  opportunityErrorDetails.ncDetails.errorDetails.negotiationDate
                                    ? { border: 'none' }
                                    : {}
                                }
                                className={`${
                                  opportunityErrorDetails.ncDetails.errorDetails.negotiationDate &&
                                  'error-input-box'
                                }`}
                                pt={{
                                  input: {
                                    root: {
                                      style: {
                                        background: 'var(--bg-table-row)',
                                        color: 'var(--text-primary)',
                                      },
                                    },
                                  },
                                }}
                              />
                            </div>
                            <div className={styles.dropdown}>
                              <label htmlFor="fprDate">
                                FPR DATE
                                {opportunityErrorDetails.ncDetails.errorDetails.fprDate && (
                                  <span style={{ color: 'red' }}>*</span>
                                )}
                              </label>
                              <Calendar
                                view="date"
                                dateFormat="mm/dd/yy"
                                onChange={e => {
                                  const formattedDate = DateConversionUtility.convertLocalToDBDateString(
                                    e.value as Date
                                  );
                                  setEditingOpportunity({
                                    ...editingOpportunity,
                                    FPRDate: formattedDate as unknown as Date,
                                  });
                                }}
                                value={DateConversionUtility.convertDBDateToLocal(
                                  editingOpportunity?.FPRDate
                                )}
                                disabled={!editingOpportunity.Negotiation || !isIterationActive}
                                showIcon
                                inputStyle={
                                  opportunityErrorDetails.ncDetails.errorDetails.fprDate
                                    ? { border: 'none' }
                                    : {}
                                }
                                className={`${
                                  opportunityErrorDetails.ncDetails.errorDetails.fprDate &&
                                  'error-input-box'
                                }`}
                                pt={{
                                  input: {
                                    root: {
                                      style: {
                                        background: 'var(--bg-table-row)',
                                        color: 'var(--text-primary)',
                                      },
                                    },
                                  },
                                }}
                              />
                            </div>
                          </div>
                          <div className={styles.row}>
                            <div className={styles.dropdown}>
                              <label htmlFor="awardee">
                                AWARDEE
                                {opportunityErrorDetails.ncDetails.errorDetails.awardee && (
                                  <span style={{ color: 'red' }}>*</span>
                                )}
                              </label>
                              <AsyncSelect
                                loadOptions={fetchSuggestionsWithDelay}
                                // onInputChange={handleInputChange}
                                onChange={option => {
                                  setEditingOpportunity({
                                    ...editingOpportunity,
                                    AwardeeID: option?.value || '',
                                  });
                                }}
                                defaultOptions
                                placeholder="Search..."
                                value={
                                  editingOpportunity.AwardeeID
                                    ? {
                                        label: editingOpportunity.AwardeeID,
                                        value: editingOpportunity.AwardeeID,
                                      }
                                    : null
                                }
                                styles={{
                                  menu: provided => ({ ...provided, zIndex: 20 }),
                                  control: (base, _state) => ({
                                    ...base,
                                    background: 'var(--bg-table-row)',
                                    border: '1px solid var(--border-primary)',
                                    borderRadius: '0.5rem',
                                  }),
                                }}
                              />
                            </div>
                            <div className={styles.dropdown}>
                              <label htmlFor="awardDate">
                                AWARD DATE
                                {opportunityErrorDetails.ncDetails.errorDetails.awardDate && (
                                  <span style={{ color: 'red' }}>*</span>
                                )}
                              </label>
                              <Calendar
                                view="date"
                                dateFormat="mm/dd/yy"
                                onChange={e => {
                                  const formattedDate = DateConversionUtility.convertLocalToDBDateString(
                                    e.value as Date
                                  );
                                  setEditingOpportunity({
                                    ...editingOpportunity,
                                    AwardDate: formattedDate as unknown as Date,
                                  });
                                }}
                                value={DateConversionUtility.convertDBDateToLocal(
                                  editingOpportunity?.AwardDate
                                )}
                                showIcon
                                disabled={!isIterationActive}
                                inputStyle={
                                  opportunityErrorDetails.ncDetails.errorDetails.awardDate
                                    ? { border: 'none' }
                                    : {}
                                }
                                className={`${
                                  opportunityErrorDetails.ncDetails.errorDetails.awardDate &&
                                  'error-input-box'
                                }`}
                                pt={{
                                  input: {
                                    root: {
                                      style: {
                                        background: 'var(--bg-table-row)',
                                        color: 'var(--text-primary)',
                                      },
                                    },
                                  },
                                }}
                              />
                            </div>
                          </div>
                          <div className={styles.row}>
                            <div className={styles.dropdown}>
                              <label htmlFor="awardedValue">
                                AWARDED VALUE
                                {opportunityErrorDetails.ncDetails.errorDetails.awardedValue && (
                                  <span style={{ color: 'red' }}>*</span>
                                )}
                              </label>
                              <InputNumber
                                value={editingOpportunity.AwardedValue || 0}
                                onChange={e => {
                                  if (Number(e.value) <= 1e9) {
                                    setEditingOpportunity({
                                      ...editingOpportunity,
                                      AwardedValue: Number(e.value) || 0,
                                    });
                                  }
                                }}
                                mode="decimal" // Enables decimal mode
                                minFractionDigits={0} // Ensures 2 decimal places
                                maxFractionDigits={2} // Restricts to 2 decimal places
                                prefix="$" // Adds the dollar symbol
                                // mode="currency"
                                maxLength={9}
                                currency="USD"
                                // locale="en-US"
                                min={0}
                                disabled={!isIterationActive}
                                inputStyle={
                                  opportunityErrorDetails.ncDetails.errorDetails.awardedValue
                                    ? { border: 'none' }
                                    : {}
                                }
                                className={`p-fluid ${
                                  opportunityErrorDetails.ncDetails.errorDetails.awardedValue &&
                                  'error-input-box'
                                }`}
                              />
                            </div>
                            <div className={styles.dropdown}>
                              <label htmlFor="gsmsBidValue">
                                GSMS BID VALUE
                                {opportunityErrorDetails.ncDetails.errorDetails.gsmsBidValue && (
                                  <span style={{ color: 'red' }}>*</span>
                                )}
                              </label>
                              <InputNumber
                                value={editingOpportunity.GSMSBidValue || 0}
                                onChange={e => {
                                  if (Number(e.value) <= 1e9) {
                                    setEditingOpportunity({
                                      ...editingOpportunity,
                                      GSMSBidValue: Number(e.value) || 0,
                                    });
                                  }
                                }}
                                mode="decimal" // Enables decimal mode
                                minFractionDigits={0} // Ensures 2 decimal places
                                maxFractionDigits={2} // Restricts to 2 decimal places
                                prefix="$" // Adds the dollar symbol
                                // mode="currency"
                                maxLength={9}
                                min={0}
                                currency="USD"
                                className={`${
                                  opportunityErrorDetails.ncDetails.errorDetails.gsmsBidValue &&
                                  'error-input-box'
                                }`}
                                inputStyle={
                                  opportunityErrorDetails.ncDetails.errorDetails.gsmsBidValue
                                    ? { border: 'none' }
                                    : {}
                                }
                                disabled={!isIterationActive}
                              />
                            </div>
                          </div>
                        </div>
                      </AccordionTab>
                    )}
                  </Accordion>
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        <EditOpportunitySkeletonLoader />
      )}
      {isAddIterationModalVisibile && editingOpportunity && comments && concepts?.Concept_info && (
        <AddIterationModal
          board={iterationType}
          visibility={isAddIterationModalVisibile}
          handleClose={handleCloseAddIterationModal}
          opportunityID={opportunityID}
          opportunityIterationID={opportunityIterationID}
        />
      )}

      {isNADWaiverModalOpen && (
        <NADWaiverModal
          visible={isNADWaiverModalOpen}
          opportunityIterationID={opportunityIterationID}
          closeModal={handleCloseNADWaiverModal}
          isIterationActive={isIterationActive}
        />
      )}
      {isSiteTransferWaiverModalOpen && (
        <SiteTransferModal
          visible={isSiteTransferWaiverModalOpen}
          opportunityIterationID={opportunityID}
          closeModal={handleCloseSiteTransferWaiverModal}
          isIterationActive={isIterationActive}
        />
      )}
      {isSeeMoreIterationModalOpen && (
        <SeeMoreIteration
          handleIterationClick={handleOldIterationClick}
          visible={isSeeMoreIterationModalOpen}
          opportunityID={opportunityID}
          closeModal={() => setIsSeeMoreIterationModalOpen(prev => !prev)}
        />
      )}

      {isOldIterationModalVisible && oldIterationId && (
        <EditOpportunity
          visible={isOldIterationModalVisible}
          viewType={viewType}
          closeModal={handleCloseOldIterationModal}
          opportunityID={opportunityID}
          opportunityIterationID={oldIterationId}
        />
      )}

      {editOpportunityConfirmationModalVisibility && (
        <ConfirmationModal
          visibility={editOpportunityConfirmationModalVisibility}
          modalHeading="Confirmation"
          modalContent="Edits done will be lost, are you sure you want to continue ?"
          handleCancel={handleCancelForConfirmationModal}
          handleConfirm={handleConfirmForConfirmationModal}
        />
      )}
    </Dialog>
  );
};

export default observer(EditOpportunity);
